/**
 * iter63 — Contact page (`/contact`).
 *
 * The old Pocketlite site had `/home/contact` which now 301s here.  We
 * expose every channel the customer might want: WhatsApp, phone, email
 * and a postal address, plus a lightweight contact form that posts to
 * the existing transactional email service (Resend) so the message
 * reaches the operator's Main Email and public Contact Email.  Designed-in
 * by reusing existing brand tokens — no new CSS, fully consistent with
 * the rest of the site.
 */
import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useContactEmail, useBusinessHours } from "../lib/contact";
import SEOHead from "../components/SEOHead";

const ADMIN_PHONE = "+91 93916 88723";
const ADMIN_WHATSAPP = "+91 62811 49443";

export default function Contact() {
  // iter71 — customer email is now live-editable from Admin → Settings.
  const email = useContactEmail();
  const ADMIN_EMAIL = email.address;
  // iter72 — admin-editable business hours line.
  const businessHours = useBusinessHours();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return; // prevent double-clicks
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name) {
      toast.error("Please share your name.");
      return;
    }
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email so we can reply.");
      return;
    }
    if (message.length < 5) {
      toast.error("Please write a short message (at least 5 characters).");
      return;
    }
    setSending(true);
    try {
      const payload = {
        name,
        email,
        phone: form.phone.trim(),
        message,
        source: "website-contact-form",
        submitted_at: new Date().toISOString(),
      };
      const res = await api.post("/contact/message", payload);
      if (res?.data?.customer_delivered) {
        toast.success("Thanks! We've emailed you a confirmation and will reply within 24 hours.");
      } else {
        toast.success("Thanks! Your message reached us — we'll reply within 24 hours.");
      }
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err) || "Sorry, we couldn't send your message. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-x py-12 lg:py-20" data-testid="contact-page">
      <SEOHead
        title="Contact Sri Durga Foods | Andhra Pickles & Spices"
        description="Reach Sri Durga Foods — WhatsApp +91 62811 49443, call +91 93916 88723 or email sridurgafoodssupport@gmail.com. Andhra pickles, pastes, spices and wood-pressed oils delivered across India."
        canonical="/contact"
      />

      <div className="max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-chili mb-3">We&apos;d love to hear from you</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark mb-4">Get in touch</h1>
        <p className="text-brand-muted max-w-2xl mb-12 leading-relaxed">
          Questions about a product, bulk orders, gifting or wholesale enquiries &mdash;
          drop us a line.  We typically reply within a few hours during business
          days from our Nellore office.
        </p>

        <div className="grid gap-6 sm:grid-cols-2 mb-14">
          <a
            href={`https://wa.me/${ADMIN_WHATSAPP.replace(/[^\d]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            className="group block p-6 border border-brand-line rounded-2xl hover:border-brand-chili transition-colors"
            data-testid="contact-whatsapp"
          >
            <MessageCircle className="w-6 h-6 text-brand-chili mb-3" strokeWidth={1.4} />
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-1">WhatsApp</div>
            <div className="font-serif text-xl text-brand-dark group-hover:text-brand-chili transition-colors">{ADMIN_WHATSAPP}</div>
          </a>
          <a
            href={`tel:${ADMIN_PHONE.replace(/\s/g, "")}`}
            className="group block p-6 border border-brand-line rounded-2xl hover:border-brand-chili transition-colors"
            data-testid="contact-phone"
          >
            <Phone className="w-6 h-6 text-brand-chili mb-3" strokeWidth={1.4} />
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-1">Call</div>
            <div className="font-serif text-xl text-brand-dark group-hover:text-brand-chili transition-colors">{ADMIN_PHONE}</div>
          </a>
          <a
            href={`mailto:${ADMIN_EMAIL}`}
            className="group block p-6 border border-brand-line rounded-2xl hover:border-brand-chili transition-colors"
            data-testid="contact-email"
          >
            <Mail className="w-6 h-6 text-brand-chili mb-3" strokeWidth={1.4} />
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-1">Email</div>
            <div className="font-serif text-xl text-brand-dark group-hover:text-brand-chili transition-colors break-all">{ADMIN_EMAIL}</div>
          </a>
          <div className="block p-6 border border-brand-line rounded-2xl" data-testid="contact-address">
            <MapPin className="w-6 h-6 text-brand-chili mb-3" strokeWidth={1.4} />
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-1">Office</div>
            <div className="font-serif text-xl text-brand-dark leading-tight">
              Sri Durga Foods<br />
              Nellore, Andhra Pradesh, India
            </div>
          </div>
        </div>

        {/* iter72 — subtle admin-editable business-hours line below contact details */}
        <p className="mb-14 -mt-6 text-sm text-brand-muted" data-testid="contact-business-hours">
          Business Hours: {businessHours}
        </p>

        <form
          onSubmit={submit}
          className="bg-brand-subtle/40 border border-brand-line rounded-2xl p-6 sm:p-8"
          data-testid="contact-form"
        >
          <h2 className="font-serif text-2xl text-brand-dark mb-6">Send us a message</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label-field">Your name</span>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                required
                data-testid="contact-name"
                className="w-full mt-1 px-4 py-3 rounded-lg border border-brand-line bg-white focus:border-brand-chili focus:outline-none transition-colors"
              />
            </label>
            <label className="block">
              <span className="label-field">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                data-testid="contact-email-input"
                className="w-full mt-1 px-4 py-3 rounded-lg border border-brand-line bg-white focus:border-brand-chili focus:outline-none transition-colors"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-field">Phone (optional)</span>
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                data-testid="contact-phone-input"
                className="w-full mt-1 px-4 py-3 rounded-lg border border-brand-line bg-white focus:border-brand-chili focus:outline-none transition-colors"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-field">Your message</span>
              <textarea
                rows={5}
                value={form.message}
                onChange={set("message")}
                required
                data-testid="contact-message"
                className="w-full mt-1 px-4 py-3 rounded-lg border border-brand-line bg-white focus:border-brand-chili focus:outline-none transition-colors"
              />
            </label>
          </div>
          <div className="mt-6">
            <button
              type="submit"
              disabled={sending}
              className="btn-primary"
              data-testid="contact-submit"
            >
              {sending ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
